<template>
  <div class="row">
    <!-- Header -->
    <div class="col-12 mb-4">
      <div class="d-flex align-items-center justify-content-between flex-wrap">
        <div>
          <h2 class="text-white font-weight-bold mb-1">{{ $t('superadmin.platformPlayersTitle') }}</h2>
          <p class="text-muted small mb-0">{{ $t('superadmin.platformPlayersSubtitle') }}</p>
        </div>
        <div class="mt-2 mt-md-0 d-flex align-items-center">
          <span class="badge badge-info px-3 py-2 font-weight-bold">
            <i class="mdi mdi-account-group mr-1"></i>
            {{ player.length }} {{ $t('nav.gamePlayer') }}
          </span>
        </div>
      </div>
    </div>

    <!-- Table Card -->
    <div class="col-12 grid-margin">
      <div class="card">
        <div class="card-body">
          <!-- Search Bar -->
          <div class="d-flex align-items-center justify-content-between mb-4 flex-wrap gap-3">
            <div class="search-box mb-2 mb-md-0" style="min-width: 320px; max-width: 440px; width: 100%;">
              <div class="input-group">
                <span class="input-group-text">
                  <i class="mdi mdi-magnify"></i>
                </span>
                <input
                  v-model.trim="search"
                  type="text"
                  class="form-control"
                  :placeholder="$currentLocale === 'id' ? 'Cari pemain, email, perusahaan...' : 'Search player, email, company...'"
                />
                <button v-if="search" class="btn btn-outline-secondary" type="button" @click="search = ''">
                  <i class="mdi mdi-close"></i>
                </button>
              </div>
            </div>
            <div class="text-muted small">
              {{ $t('common.all') }}: <strong class="text-white">{{ filteredItems.length }}</strong>
            </div>
          </div>

          <!-- Table -->
          <div class="table-responsive">
            <table class="table table-hover text-white align-middle" style="table-layout: fixed; width: 100%;">
              <thead>
                <tr class="text-muted text-uppercase small" style="border-bottom: 1px solid rgba(255,255,255,0.1);">
                  <th style="width: 40px;">No</th>
                  <th style="width: 220px;">{{ $t('common.name') }}</th>
                  <th style="width: 170px;">{{ $t('superadmin.assignedCompany') }}</th>
                  <th style="width: 130px;">{{ $t('superadmin.squadDivision') }}</th>
                  <th style="width: 110px;">{{ $t('superadmin.userLevel') }} & XP</th>
                  <th style="width: 100px;">{{ $t('superadmin.goldBalance') }}</th>
                  <th style="width: 105px;">{{ $t('superadmin.joinDate') }}</th>
                  <th style="width: 95px;">{{ $t('common.status') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(data, index) in paginatedPlayer"
                  :key="data.id"
                  style="border-bottom: 1px solid rgba(255,255,255,0.05);"
                >
                  <td class="text-muted">{{ index + 1 + (currentPage - 1) * itemsPerPage }}</td>
                  <td>
                    <div class="d-flex align-items-center">
                      <div
                        class="rounded-circle mr-2 d-flex align-items-center justify-content-center bg-dark text-info font-weight-bold flex-shrink-0"
                        style="width: 32px; height: 32px; border: 1px solid rgba(0, 144, 231, 0.3);"
                      >
                        {{ (data.name || 'P').charAt(0).toUpperCase() }}
                      </div>
                      <div class="overflow-hidden">
                        <div class="font-weight-bold text-white text-truncate" :title="data.name">{{ data.name || '-' }}</div>
                        <small class="text-muted d-block text-truncate" :title="data.email">{{ data.email || '-' }}</small>
                      </div>
                    </div>
                  </td>
                  <td>
                    <span class="badge badge-outline-warning px-2 py-1 text-truncate d-inline-block" style="max-width: 155px;" :title="data.company_name || (data.company && data.company.name) || '-'">
                      <i class="mdi mdi-domain mr-1"></i>
                      {{ data.company_name || (data.company && data.company.name) || '-' }}
                    </span>
                  </td>
                  <td>
                    <span v-if="data.division" class="badge badge-outline-info text-truncate d-inline-block" style="max-width: 120px;" :title="data.division.name">
                      {{ data.division.name }}
                    </span>
                    <span v-else class="text-muted small">-</span>
                  </td>
                  <td>
                    <div class="font-weight-bold text-success">{{ $currentLocale === 'id' ? 'Tingkat' : 'Level' }} {{ data.level || 1 }}</div>
                    <small class="text-muted">{{ (data.xp || 0).toLocaleString() }} XP</small>
                  </td>
                  <td>
                    <span class="text-warning font-weight-bold">
                      <i class="mdi mdi-circle-multiple mr-1"></i>
                      {{ (data.gold || 0).toLocaleString() }}
                    </span>
                  </td>
                  <td class="text-muted small">{{ formatDate(data.created_at) }}</td>
                  <td>
                    <span
                      class="badge"
                      :class="isUserActive(data.status) ? 'badge-success' : 'badge-danger'"
                    >
                      {{ isUserActive(data.status) ? $t('superadmin.statusActive') : $t('common.inactive') }}
                    </span>
                  </td>
                </tr>

                <tr v-if="paginatedPlayer.length === 0">
                  <td colspan="8" class="text-center py-5">
                    <EmptyState
                      icon="mdi-account-multiple-outline"
                      :title="$t('superadmin.noPlayersFound')"
                      :description="$t('superadmin.noPlayersDesc')"
                    />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Pagination -->
          <div v-if="filteredItems.length > itemsPerPage" class="mt-4">
            <PaginationComponents
              :totalItems="filteredItems.length"
              :itemsPerPage="itemsPerPage"
              :currentPage="currentPage"
              @page-changed="onPageChanged"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getPlayerAdmin } from '@/services/playerService';
import moment from 'moment';
import PaginationComponents from '@/components/PaginationComponents.vue';
import EmptyState from '@/components/EmptyState.vue';

export default {
  name: 'ListPlayerAdmin',
  components: {
    PaginationComponents,
    EmptyState,
  },
  data() {
    return {
      search: '',
      player: [],
      currentPage: 1,
      itemsPerPage: 15,
      loading: false,
    };
  },
  computed: {
    filteredItems() {
      if (!this.search) return this.player;
      const q = this.search.toLowerCase();
      return this.player.filter((item) => {
        const nameMatch = (item.name || '').toLowerCase().includes(q);
        const emailMatch = (item.email || '').toLowerCase().includes(q);
        const companyMatch = (
          item.company_name ||
          (item.company && item.company.name) ||
          ''
        )
          .toLowerCase()
          .includes(q);
        return nameMatch || emailMatch || companyMatch;
      });
    },
    paginatedPlayer() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.filteredItems.slice(start, end);
    },
  },
  mounted() {
    this.fetchData();
  },
  methods: {
    async fetchData() {
      this.loading = true;
      try {
        const resp = await getPlayerAdmin();
        this.player = resp.data || resp || [];
      } catch (err) {
        console.error('Error fetching platform players:', err);
      } finally {
        this.loading = false;
      }
    },
    isUserActive(status) {
      if (!status) return true;
      const s = status.toLowerCase();
      return s === 'active' || s === '1' || s === 1;
    },
    formatDate(date) {
      if (!date) return '-';
      return moment(date).format('DD MMM YYYY');
    },
    onPageChanged(page) {
      this.currentPage = page;
    },
  },
};
</script>
